/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import { Layout, Spinner } from '@sonarsource/echoes-react';
import { uniq } from 'lodash';
import { useIntl } from 'react-intl';
import { useFlags } from '~adapters/helpers/feature-flags';
import { useMeasuresComponentQuery } from '~adapters/queries/measures';
import { BasicSeparator } from '~design-system';
import { ProjectPageTemplate } from '~shared/components/pages/ProjectPageTemplate';
import { useScaOverviewMetrics } from '~shared/helpers/sca';
import { isDefined } from '~shared/helpers/types';
import { PullRequest } from '~shared/types/branch-like';
import { ComponentNavBindingStatus } from '~sq-server-commons/components/nav/ComponentNavBindingStatus';
import { AnalysisStatus } from '~sq-server-commons/components/overview/AnalysisStatus';
import IgnoredConditionWarning from '~sq-server-commons/components/overview/IgnoredConditionWarning';
import LastAnalysisLabel from '~sq-server-commons/components/overview/LastAnalysisLabel';
import QGStatus from '~sq-server-commons/components/overview/QualityGateStatus';
import '~sq-server-commons/components/overview/styles.css';
import { useAvailableFeatures } from '~sq-server-commons/context/available-features/withAvailableFeatures';
import {
  enhanceConditionWithMeasure,
  enhanceMeasuresWithMetrics,
} from '~sq-server-commons/helpers/measures';
import { useBranchStatusQuery } from '~sq-server-commons/queries/branch';
import { useComponentQualityGateQuery } from '~sq-server-commons/queries/quality-gates';
import { Feature } from '~sq-server-commons/types/features';
import { Component } from '~sq-server-commons/types/types';
import { PR_METRICS } from '~sq-server-commons/utils/overview-utils';
import { PRLink } from '../../nav/component/branch-like/PRLink';
import MeasuresCardPanel from './MeasuresCardPanel';
import { PullRequestMetaContentHeader } from './PullRequestMetaContentHeader';
import PullRequestMetaTopBar from './PullRequestMetaTopBar';
import SonarLintAd from './SonarLintAd';

interface Props {
  component: Component;
  pullRequest: PullRequest;
}

export function PullRequestOverview(props: Readonly<Readonly<Props>>) {
  const { component, pullRequest } = props;
  const intl = useIntl();
  const {
    data: { conditions, ignoredConditions, status } = {},
    isLoading: isLoadingBranchStatusesData,
  } = useBranchStatusQuery(component);

  const { data: qualityGate, isLoading: isLoadingQualityGate } = useComponentQualityGateQuery(
    component.key,
  );

  const { frontEndEngineeringEnableSidebarNavigation } = useFlags();
  const { hasFeature } = useAvailableFeatures();
  const hasSca = hasFeature(Feature.Sca);
  const hasBranchSupport = hasFeature(Feature.BranchSupport);
  const scaOverviewMetrics = useScaOverviewMetrics(hasSca);
  const pullRequestOverviewMetrics = [...PR_METRICS, ...scaOverviewMetrics];
  const pageTitle = intl.formatMessage({ id: 'overview.page' });

  const { data: componentMeasures, isLoading: isLoadingMeasures } = useMeasuresComponentQuery(
    {
      componentKey: component.key,
      metricKeys: uniq([
        ...pullRequestOverviewMetrics,
        ...(conditions?.map((c) => c.metric) ?? []),
      ]),
      branchLike: pullRequest,
    },
    { enabled: !isLoadingBranchStatusesData },
  );

  const measures = componentMeasures
    ? enhanceMeasuresWithMetrics(
        componentMeasures.component.measures ?? [],
        componentMeasures.metrics,
      )
    : [];

  const isLoading = isLoadingBranchStatusesData || isLoadingMeasures || isLoadingQualityGate;

  if (isLoading) {
    return (
      <ProjectPageTemplate disableQualityGateStatus title={pageTitle}>
        <Spinner isLoading />
      </ProjectPageTemplate>
    );
  }

  if (conditions === undefined) {
    return null;
  }

  const enhancedConditions = conditions
    .map((c) => enhanceConditionWithMeasure(c, measures))
    .filter(isDefined);

  return (
    <ProjectPageTemplate
      actions={
        <Layout.ContentHeader.Actions>
          <ComponentNavBindingStatus
            component={component}
            prLinkAddon={
              hasBranchSupport && <PRLink component={component} pullRequest={pullRequest} />
            }
          />
        </Layout.ContentHeader.Actions>
      }
      disableQualityGateStatus
      metadata={<PullRequestMetaContentHeader measures={measures} pullRequest={pullRequest} />}
      pageClassName="it__pr-overview"
      title={pageTitle}
    >
      {!frontEndEngineeringEnableSidebarNavigation && (
        <>
          <PullRequestMetaTopBar measures={measures} pullRequest={pullRequest} />
          <BasicSeparator className="sw-my-4" />
        </>
      )}

      {ignoredConditions && <IgnoredConditionWarning />}

      <div className="sw-flex sw-justify-between sw-items-start sw-my-6">
        <QGStatus status={status} />
        {!frontEndEngineeringEnableSidebarNavigation && (
          <LastAnalysisLabel analysisDate={pullRequest.analysisDate} />
        )}
      </div>

      <AnalysisStatus className="sw-mb-4" component={component} />

      <MeasuresCardPanel
        component={component}
        conditions={enhancedConditions}
        measures={measures}
        pullRequest={pullRequest}
        qualityGate={qualityGate}
      />

      <SonarLintAd status={status} />
    </ProjectPageTemplate>
  );
}
